import type { Workspace } from "@salon-pos/shared";
import { useState } from "react";
import { api } from "../api";
import { useAuth } from "../auth";
import { useI18n } from "../i18n";
import { Confirm, Dialog, ErrorNote, Field, errorText, useToast } from "../ui";
import { TabletTip } from "./TabletTip";

const IS_NATIVE = import.meta.env.VITE_CLIENT === "native";

/**
 * (Task U, rule D) Persistent owner prompt while the salon can still ring up sales without a cashier login:
 * transition period (existing salons) or a branch with no cashier login yet. One click opens "create cashier login".
 */
export function CashierBanner({ ws }: { ws: Workspace }) {
  const { me, refresh } = useAuth();
  const { t } = useI18n();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [ask, setAsk] = useState(false);
  const [busy, setBusy] = useState(false);
  const co = me?.checkout;
  if (IS_NATIVE || !me || !co || !co.promptCashier || ws !== "owner") return null;
  const switchNow = async () => {
    setBusy(true);
    try { await api("/api/checkout/mode", { method: "POST", body: { mode: "cashier_only" } }); toast(t("tu.co.switched")); setAsk(false); await refresh(); }
    catch (e) { toast(errorText(e)); } finally { setBusy(false); }
  };
  return (
    <div className="banner banner-warn" role="status" data-testid="cashier-banner">
      <span>{co.mode === "transition" ? t("tu.co.bannerTransition") : t("tu.co.bannerNoCashier")}</span>
      <span className="row" style={{ gap: 8, flexWrap: "wrap" }}>
        <button className="btn btn-primary btn-sm" onClick={() => setOpen(true)}>{t("tu.co.create")}</button>
        {co.mode === "transition" && co.cashierCount > 0 && <button className="btn btn-quiet btn-sm" onClick={() => setAsk(true)}>{t("tu.co.switch")}</button>}
      </span>
      {open && <CreateCashierDialog onClose={() => setOpen(false)} onDone={refresh} />}
      <Confirm open={ask} title={t("tu.co.switch")} body={t("tu.co.switchConfirm")} confirmLabel={t("tu.co.switch")} busy={busy} onConfirm={() => void switchNow()} onCancel={() => setAsk(false)} />
    </div>
  );
}

/** suggest name+cashier-<code>@domain of the owner's e-mail (plus-addressing reaches the owner's own inbox) */
function suggestEmail(owner: string | null | undefined, code: string) {
  const m = /^([^@+]+)(?:\+[^@]*)?@(.+)$/.exec(owner ?? "");
  return m ? m[1] + "+cashier-" + code.toLowerCase().replace(/[^a-z0-9]/g, "") + "@" + m[2] : "";
}

/** One-click cashier login (centred modal): name, login e-mail and branches prefilled; the temporary password is shown once. */
export function CreateCashierDialog({ onClose, onDone, branchId }: { onClose: () => void; onDone?: () => Promise<void> | void; branchId?: string }) {
  const { me } = useAuth();
  const { t } = useI18n();
  const branches = me?.branches ?? [];
  const first = branches.find((b) => b.id === (branchId ?? me?.activeBranchId)) ?? branches[0];
  const [name, setName] = useState(first ? "Front desk · " + first.name : "Front desk");
  const [email, setEmail] = useState(suggestEmail(me?.user.email, first?.code ?? "desk"));
  const [ids, setIds] = useState<string[]>(first ? [first.id] : []);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ email: string; password: string } | null>(null);
  const save = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api<{ password: string; cashier: { email: string } }>("/api/team/cashiers", { method: "POST", body: { name: name.trim(), email: email.trim(), branchIds: ids } });
      setDone({ email: r.cashier.email, password: r.password });
      await onDone?.();
    } catch (e) { setErr(errorText(e)); } finally { setBusy(false); }
  };
  if (done) return (
    <Dialog open size="md" title={t("tu.co.done")} onClose={onClose} cancel={false} footer={<button className="btn btn-primary" onClick={onClose}>OK</button>}>
      <p><strong>{done.email}</strong></p>
      <p className="num" data-testid="cashier-password">{t("tu.co.password", { pw: done.password })}</p>
      <p className="hint">{t("tu.co.passwordHint")}</p>
      <TabletTip compact />
    </Dialog>
  );
  return (
    <Dialog open size="md" title={t("tu.co.title")} onClose={onClose} dirty={false}
      footer={<button className="btn btn-primary" disabled={busy || !name.trim() || !email.includes("@") || !ids.length} onClick={() => void save()}>{t("tu.co.create")}</button>}>
      <p className="muted">{t("tu.co.hint")}</p>
      <TabletTip />
      <Field label={t("tu.co.name")}><input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} autoFocus /></Field>
      <Field label={t("tu.co.email")} hint={t("tu.co.emailHint")}><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={200} /></Field>
      {branches.length > 1 && (
        <Field label={t("tu.co.branches")}>
          <div className="stack">{branches.map((b) => (
            <label key={b.id} className="check"><input type="checkbox" checked={ids.includes(b.id)} onChange={(e) => setIds(e.target.checked ? [...ids, b.id] : ids.filter((x) => x !== b.id))} /> {b.name}</label>
          ))}</div>
        </Field>
      )}
      <ErrorNote text={err} />
    </Dialog>
  );
}
